import { useState, useEffect } from 'react';
import { api } from '../api/client';

interface OperationsData {
  daily_routines: string;
  templates: string;
  prompt_library: string;
  artifacts_logic: string;
}

const DEFAULT_OPS: OperationsData = {
  daily_routines: '',
  templates: '',
  prompt_library: '',
  artifacts_logic: '',
};

type OpsTab = keyof OperationsData;

const TABS: { key: OpsTab; label: string; icon: string; description: string; placeholder: string }[] = [
  {
    key: 'daily_routines',
    label: 'Daily Routines',
    icon: '🔄',
    description: 'Chuỗi hoạt động hàng ngày được chuẩn hóa. AI sẽ dùng để nhắc nhở và gợi ý theo thói quen của bạn.',
    placeholder: `# Daily Routines

## Buổi Sáng (9:00 - 9:30)
- Review email + Slack, phân loại priority
- Check OKR progress dashboard
- Block thời gian deep work

## Buổi Trưa (13:00 - 13:15)
- Daily standup cập nhật team
- Review task backlog

## Cuối Ngày (17:30)
- Ghi Growth Log hôm nay
- Chuẩn bị agenda ngày mai
- Clear inbox về 0`,
  },
  {
    key: 'templates',
    label: 'Templates',
    icon: '📝',
    description: 'Các mẫu tài liệu chuẩn hóa cho công việc. AI sẽ dùng làm khuôn mẫu khi tạo nội dung mới.',
    placeholder: `# Templates Thường Dùng

## PRD Template
**Problem Statement:** [1 câu mô tả vấn đề]
**Target Users:** [Ai sẽ dùng tính năng này]
**Success Metrics:** [KPIs cụ thể]
**Solution:** [Mô tả giải pháp]
**Timeline:** [Milestones]

## Weekly Report Template
**Accomplished this week:** 
**Blockers:**
**Next week plan:**

## Email Thuyết Phục
Subject: [Lợi ích cụ thể] - Action needed by [ngày]
...`,
  },
  {
    key: 'prompt_library',
    label: 'Prompt Library',
    icon: '💬',
    description: 'Bộ sưu tập prompt cá nhân hóa hiệu quả nhất. AI sẽ dùng làm reference khi bạn cần prompt tương tự.',
    placeholder: `# Prompt Library

## Phân Tích Tài Liệu
"Hãy đọc [tài liệu] và tóm tắt theo format: 
1. Ý chính (3 bullet)
2. Action items cho tôi  
3. Rủi ro cần chú ý"

## Viết Email Chuyên Nghiệp
"Viết email [mục đích], tone: [professional/friendly], 
người nhận: [chức danh], độ dài: tối đa 200 từ"

## Phản Biện Ý Tưởng
"Hãy đóng vai devil's advocate và chỉ ra 5 điểm yếu 
của kế hoạch sau: [kế hoạch]"`,
  },
  {
    key: 'artifacts_logic',
    label: 'Artifacts Logic',
    icon: '⚙️',
    description: 'Quy tắc xử lý và chuẩn hóa output AI. Định nghĩa cách AI format, cấu trúc, và giao nộp kết quả.',
    placeholder: `# Artifacts Logic

## Quy Tắc Output Mặc Định
- Luôn trả lời bằng tiếng Việt trừ khi tôi hỏi tiếng Anh
- Ưu tiên bullet points thay vì đoạn văn dài
- Kết thúc mỗi phân tích bằng "Đề xuất hành động tiếp theo:"

## Format Tài Liệu
- Báo cáo: Tóm tắt → Chi tiết → Kết luận
- Plan: Mục tiêu → Milestones → Risks → Resources

## Naming Convention
- File output: [YYYY-MM-DD]_[topic]_[version].md
- Meeting notes: MOM_[project]_[date]

## Quy Tắc Tham Chiếu
- Khi trích dẫn, ghi rõ "Theo [tài liệu/trang]:"
- Số liệu phải kèm nguồn và ngày thu thập`,
  },
];

export function OperationsPage() {
  const [ops, setOps] = useState<OperationsData>(DEFAULT_OPS);
  const [activeTab, setActiveTab] = useState<OpsTab>('daily_routines');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.getOperations()
      .then(data => setOps({ ...DEFAULT_OPS, ...data }))
      .catch(() => {/* not set yet */})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.saveOperations(ops);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      alert('Lưu thất bại: ' + (e.message ?? 'Lỗi không xác định'));
    } finally {
      setSaving(false);
    }
  };

  const currentTab = TABS.find(t => t.key === activeTab)!;

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px', flexDirection: 'column' }}>
        <div className="spinner" style={{ width: 32, height: 32 }} />
        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Đang tải Operations Hub...</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '0' }}>

      {/* Page Header */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div>
          <h1 className="page-title">⚙️ Operations Hub</h1>
          <p className="page-subtitle">
            Chuẩn hóa quy trình làm việc — Templates, Prompts, Daily Routines, Artifacts Logic. AI sẽ học và áp dụng phong cách của bạn.
          </p>
        </div>
        <button
          className="btn btn--primary"
          onClick={handleSave}
          disabled={saving}
          style={{ minWidth: 120 }}
        >
          {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : saved ? '✅ Đã lưu!' : '💾 Lưu'}
        </button>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '4px', paddingBottom: '16px', flexShrink: 0, flexWrap: 'wrap' }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              borderRadius: '8px',
              border: `1px solid ${activeTab === tab.key ? 'var(--accent-primary)' : 'var(--border)'}`,
              background: activeTab === tab.key ? 'rgba(124,58,237,0.15)' : 'var(--bg-glass)',
              color: activeTab === tab.key ? '#c084fc' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: activeTab === tab.key ? 600 : 400,
              transition: 'all 0.2s',
            }}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
            {ops[tab.key] && (
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent-green)', flexShrink: 0 }} />
            )}
          </button>
        ))}
      </div>

      {/* Content area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px', overflow: 'hidden' }}>

        {/* Tab description */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          background: 'var(--bg-glass)',
          border: '1px solid var(--border)',
          borderRadius: '10px',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: '24px' }}>{currentTab.icon}</span>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}>{currentTab.label}</div>
            <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', lineHeight: 1.5 }}>{currentTab.description}</div>
          </div>
        </div>

        {/* Markdown editor */}
        <textarea
          className="textarea"
          value={ops[activeTab]}
          onChange={e => setOps(prev => ({ ...prev, [activeTab]: e.target.value }))}
          placeholder={currentTab.placeholder}
          style={{
            flex: 1,
            resize: 'none',
            fontSize: '13.5px',
            lineHeight: 1.7,
            padding: '16px',
            background: 'var(--bg-glass)',
            border: '1px solid var(--border)',
            borderRadius: '10px',
            fontFamily: 'var(--font-mono)',
          }}
        />

        {/* Word count and tips */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            {ops[activeTab] ? `${ops[activeTab].split(/\s+/).filter(Boolean).length} từ · ${ops[activeTab].length} ký tự` : 'Markdown được hỗ trợ'}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            💾 Lưu để AI áp dụng ngay phiên hỏi đáp tiếp theo
          </div>
        </div>
      </div>
    </div>
  );
}
